/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useState, useCallback } from 'react';
import { AsyncCmdbUpsellPage } from '@jira/servicedesk__cmdb-upsell-page/src/AsyncCmdbUpsellPage.tsx';
import { getRootPath } from '@jira/servicedesk__insight-common-utils-insight-urls/src/getLandingPageUrl.tsx';
import { useFormattedMessageAndIcon } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-formatted-message-and-icon/index.tsx';
import {
	L1_MENU_ID,
	type ConfigurableMenuId,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { useConfigurableMessageAndIconMap } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
import { HideL1MenuItemSection } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/hide-l1-menu-item-action/index.tsx';
import { MENU_ID_ASSETS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { Nav4MenuLinkItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-menu-link-item/index.tsx';
import Nav4MenuButtonItem from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/button-menu-item-with-analytics/index.tsx';
import { MoreNavMenuButton } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/more-nav-menu-button/main.tsx';
import { NAVIGATION_ITEM } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/action-subject-type.tsx';
import { FIXED_ITEM } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import { useIsClientReady } from '@jira/ssr__ssr-toolkit/src/hooks/use-is-client-ready.tsx';
import Drawer from '@atlaskit/drawer/drawer';
import { DrawerSidebar } from '@atlaskit/drawer/drawer-sidebar';
import { DrawerContent } from '@atlaskit/drawer/drawer-content';
import { DrawerCloseButton } from '@atlaskit/drawer/drawer-close-button';
import Lozenge from '@atlaskit/lozenge/lozenge';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';

const openAssetsApp = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-assets.ui.assets-app-item.open-assets-app',

	defaultMessage: 'Open Assets',
	description: 'Label for a link, that will open the Assets app when clicked on',
});

const upsellLozengeText = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-assets.ui.assets-app-item.upsell-lozenge-text',

	defaultMessage: 'Premium',
	description:
		'A Lozenge located after Assets label to attract non premium customer explore Assets',
});

const actionsOnHover = (
	<MoreNavMenuButton MenuItems={() => <HideL1MenuItemSection menuId={L1_MENU_ID.ASSETS} />} />
);
const linkAnalytics = {
	actionSubjectId: NAVIGATION_ITEM,
	itemId: MENU_ID_ASSETS,
	level: 1,
} as const;
const upsellButtonAnalytics = { ...linkAnalytics, componentType: FIXED_ITEM } as const;

const AssetsExternalLink = () => {
	const { formattedMessage: assetsMessage, icon: assetsIcon } = useConfigurableMessageAndIconMap(
		L1_MENU_ID.ASSETS,
	);
	const { icon: openExternalLinkIcon } = useFormattedMessageAndIcon(
		openAssetsApp,
		LinkExternalIcon,
	);

	return (
		<Nav4MenuLinkItem
			href={getRootPath()}
			target="_blank"
			menuId={MENU_ID_ASSETS}
			analytics={linkAnalytics}
			elemBefore={assetsIcon}
			elemAfter={openExternalLinkIcon}
			actionsOnHover={actionsOnHover}
		>
			{assetsMessage}
		</Nav4MenuLinkItem>
	);
};

/* hard set assetsIcon to L1_MENU_ID.GLOBAL_APP_ASSETS and remove this prop on cleanup of:
 * assets_as_an_app_v2 &&
 */
type AssetsUpsellButtonProps = {
	configurableMenuId?: ConfigurableMenuId;
	lozengeText?: string;
};

export const AssetsUpsellButton = ({
	configurableMenuId,
	lozengeText,
}: AssetsUpsellButtonProps): React.JSX.Element => {
	const [isOpen, setIsOpen] = useState(false);
	// The Drawer reads `document` on render, which is unsafe during SSR. Behind
	// assets-platform-landing-page (which surfaces this upsell in SSR-rendered nav) defer mounting it
	// until the client is ready; otherwise keep the prior always-mounted behaviour.
	const isClientReady = useIsClientReady();
	const canRenderDrawer = !fg('assets-platform-landing-page') || isClientReady;
	const { formatMessage } = useIntl();
	const configurableMessageIcon = configurableMenuId ?? L1_MENU_ID.ASSETS;
	const { formattedMessage: assetsMessage, icon: assetsIcon } =
		useConfigurableMessageAndIconMap(configurableMessageIcon);
	const upsellText = lozengeText ?? formatMessage(upsellLozengeText);

	const handleClose = useCallback(() => {
		setIsOpen(false);
	}, []);

	return (
		<>
			<Nav4MenuButtonItem
				elemBefore={assetsIcon}
				elemAfter={
					<Lozenge
						// eslint-disable-next-line jira/integration/test-id-by-folder-structure
						testId="navigation-apps-sidebar-nav4-sidebars-content-assets.ui.assets-app-item.upsell-lozenge"
						appearance="discovery"
					>
						{upsellText}
					</Lozenge>
				}
				analytics={upsellButtonAnalytics}
				onClick={() => {
					setIsOpen(true);
				}}
				actionsOnHover={configurableMessageIcon === L1_MENU_ID.ASSETS ? actionsOnHover : null}
				// eslint-disable-next-line jira/integration/test-id-by-folder-structure
				testId="navigation-apps-sidebar-nav4-sidebars-content-assets.ui.assets-app-item.nav-4-menu-button-item"
			>
				{assetsMessage}
			</Nav4MenuButtonItem>
			{/* Rendering the Drawer outside the menu button to not trigger its onClick handler when the drawer back button is pressed */}
			{canRenderDrawer && (
				<Drawer
					isOpen={isOpen}
					onClose={handleClose}
					width="full"
					// eslint-disable-next-line jira/integration/test-id-by-folder-structure
					testId="navigation-apps-sidebar-nav4-sidebars-content-assets.ui.assets-app-item.drawer"
					label={assetsMessage}
				>
					<DrawerSidebar>
						<DrawerCloseButton />
					</DrawerSidebar>
					<DrawerContent>
						<AsyncCmdbUpsellPage />
					</DrawerContent>
				</Drawer>
			)}
		</>
	);
};

export const AssetsAppItem = ({ showUpsell }: { showUpsell: boolean }): React.JSX.Element => {
	if (showUpsell) {
		return <AssetsUpsellButton />;
	}
	return <AssetsExternalLink />;
};
